import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PublicIcon from '@mui/icons-material/Public';
import {
    Box,
    Divider,
    Grid,
    styled,
    Typography,
    useTheme
} from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { getCompany } from '../../../services/company';
import { getCompanyJobs, getUserJobs } from '../../../services/jobs';
import { UserContext } from '../../context/UserContext';
import Button, {
    BackButton,
    TextButton
} from '../../global/components/Button/Button';
import { BaseCard, BaseContainer, ProfilePicture } from '../../global/Core';
import JobCard from '../Dashboard/CandidateDashboard/SummaryJobCard';

import type { Company } from '../../../types/company';
import type { Job } from '../../../types/job';

const StyledBaseCard = styled(BaseCard)`
    margin-bottom: 2rem;
`;

const CardHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    min-height: 2rem;
`;

const CompanyHeader = styled('div')`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 1.5rem;
`;

const StatSection = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding-top: 2rem;
    padding-bottom: 2rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
`;

const SummarySection = styled(StatSection)`
    gap: 1.5rem;
    padding-bottom: 1rem;
`;

const CompanyStat = styled('div')`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`;

export default function BusinessDetails() {
    const [company, setCompany] = React.useState<Company>();
    const [jobs, setJobs] = React.useState<Job[]>([]);
    const theme = useTheme();
    const { user } = useContext(UserContext);
    const history = useHistory();
    // const location = useLocation();
    const { companyID } = useParams<{ companyID: string }>();

    const isBusiness = user.levylRole === 'business';

    useEffect(() => {
        getCompany(parseInt(companyID, 10)).then((res: Company) => {
            setCompany(res);
        });
        if (user.levylRole === 'candidate' && user.id) {
            getUserJobs(user.id).then((res: Job[]) => {
                setJobs(
                    res.filter(
                        (job) => job.companyID === parseInt(companyID, 10)
                    )
                );
            });
        } else {
            getCompanyJobs(parseInt(companyID, 10)).then((res: Job[]) => {
                setJobs(res);
            });
        }
    }, [user.id, user.levylRole, companyID]);

    const companyStats = [
        {
            title: 'Location',
            value: company?.location,
            icon: LocationOnIcon
        },
        {
            title: 'Industry',
            value: company?.industry,
            icon: BusinessIcon
        },
        {
            title: 'Company Size',
            value: `${company?.size} employees`,
            icon: PeopleAltIcon
        }
    ];

    const handleEditCompany = () => {
        history.push(`/edit/${user.userID}/business/${companyID}/companypage`);
    };

    const handleEditJob = (jobId: number) => {
        history.push(`/edit/${user.userID}/job/${companyID}/${jobId}/details`);
    };

    const handleGoToDashboard = () => {
        history.push(`/secure/${user.userID}/business/dashboard`);
    };

    return (
        <BaseContainer>
            {isBusiness && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant='h4' fontWeight='600' sx={{ mb: 1 }}>
                        {'Your Company Profile is Ready!'}
                    </Typography>
                    <Typography variant='body1' color='text.secondary'>
                        {
                            'Review the generated information below. You can edit any section or proceed to your dashboard.'
                        }
                    </Typography>
                </Box>
            )}

            <StyledBaseCard variant='outlined'>
                <CardHeader>
                    <BackButton
                        onClick={() => {
                            history.goBack();
                        }}
                        startIcon={<KeyboardBackspaceIcon />}
                        color='primary'
                    />
                    {isBusiness && (
                        <BackButton
                            onClick={handleEditCompany}
                            startIcon={<EditIcon />}
                            color='primary'
                        />
                    )}
                </CardHeader>
                <CompanyHeader>
                    <ProfilePicture
                        src={company?.logoURL}
                        size={2}
                        sx={{ mr: 1 }}
                    />
                    <Typography
                        variant={'h5'}
                        color={'info.main'}
                        fontWeight={700}
                    >
                        {company?.name}
                    </Typography>
                </CompanyHeader>
                <StatSection>
                    {companyStats.map((stat, index) => (
                        <CompanyStat key={index}>
                            <stat.icon
                                style={{ color: theme.palette.text.secondary }}
                            />
                            <Typography variant={'body2'} component={'span'}>
                                {`${stat.title}: `}
                                <Typography
                                    variant={'body2'}
                                    color={'text.secondary'}
                                    fontWeight={400}
                                    component={'span'}
                                >
                                    {stat.value}
                                </Typography>
                            </Typography>
                        </CompanyStat>
                    ))}
                    <CompanyStat>
                        <PublicIcon color={'primary'} />
                        <Typography variant={'body2'} component={'span'}>
                            <TextButton
                                variant='text'
                                link={company?.websiteURL}
                            >
                                {company?.websiteURL}
                            </TextButton>
                        </Typography>
                    </CompanyStat>
                </StatSection>
                <Divider variant='middle' flexItem />
                <SummarySection>
                    <Typography variant={'h6'}>{'Description'}</Typography>
                    <Typography
                        variant={'body2'}
                        dangerouslySetInnerHTML={{
                            __html: company?.description ?? ''
                        }}
                    ></Typography>
                </SummarySection>
            </StyledBaseCard>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    mt: 6
                }}
            >
                <Typography variant={'h6'} color={'text.secondary'}>
                    {'Current Company Postings'}
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {jobs.map((job, index) => (
                    <Grid item xs={12} key={index}>
                        {isBusiness ? (
                            <Box sx={{ position: 'relative' }}>
                                <JobCard
                                    job={job}
                                    editJob={() => handleEditJob(job.id)}
                                />
                            </Box>
                        ) : (
                            <JobCard job={job} />
                        )}
                    </Grid>
                ))}
            </Grid>

            {isBusiness && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 4
                    }}
                >
                    <Button variant='contained' onClick={handleGoToDashboard}>
                        {'Go to Dashboard'}
                    </Button>
                </Box>
            )}
        </BaseContainer>
    );
}
